
import { useState, useMemo } from 'react';
import { useParams } from 'react-router';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { Grid, Button, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, TextField, Typography, Box, Tooltip, InputAdornment,Checkbox } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import ServerErrorModal from './ServerErrorModal';
import { updateClaimApi, verifyAmountApi } from '../apiServices';
import {  backendDateFormat, fieldDateFormat } from '../../../constants';
import { PageLoader } from '../../../shared_elements';
import { regexConstants } from '../../../constants/regEx';
import { isValidYearRange } from '../../../utils/helper';
import { trackActivity } from '../../../utils/mixpanel';
import {  globalPutService } from '../../../utils/globalApiServices';
import { checkApiStatus, getLlpClaimEventAmount, numberWithCommas, getTextFieldRows, roundAmount } from '../../../utils';
import { hasLlpEvent } from '../utils';

const VerifyAmountModal = ({ modeType, shopVisit, claim, verifyAmount, onToggleModal, updatedClaimData,  onTabChange, getResponseBack }) => {
  const isLlpEvent = hasLlpEvent(shopVisit);
  const { leaseBasicDetails } = useSelector((state) => state.leases);
  const { leaseSlug } = useParams();
  const params = { leaseSlug: leaseSlug };
  const assetData = leaseBasicDetails?.asset
  const updatedAsset = { asset_slug: assetData?.asset_type === 1 && shopVisit?.major_assembly && Object.keys(shopVisit?.major_assembly).length > 0 ? shopVisit?.major_assembly?.asset_slug : assetData?.slug, }
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setLoading] = useState({ skeletonLoader: false, pageLoader: false, buttonLoader: false });
  const [payout, setPayout] = useState(updatedClaimData?.data || claim)
  const [serverError, setServerError] = useState({});
  const [error, setError] = useState({});
  const llpAmountPaid = getLlpClaimEventAmount(claim?.claim_events, 'claim_amount') || 0;
  const isClaimCarryForward = payout?.is_claim_carry_forward === undefined || payout?.is_claim_carry_forward === null ? true : payout.is_claim_carry_forward;

  // Engine (asset_type === 2) module payout amounts, keyed by `${shop_visit_event_id}_${shop_visit_event_module_id}`.
  const nonLlpModules = useMemo(() => {
    if (!Array.isArray(claim?.claim_events)) return [];
    const modules = [];
    claim.claim_events.forEach((event) => {
      if (event?.maintenance_program?.is_llp_event === false) {
        (event?.modules || []).forEach((mod) => {
          modules.push({ ...mod, shop_visit_event_id: event?.shop_visit_event_id });
        });
      }
    });
    return modules;
  }, [claim?.claim_events]);

  const nonLlpEventsWithoutModules = useMemo(() => {
    if (!Array.isArray(claim?.claim_events)) return [];
    return claim.claim_events.filter((event) => event?.maintenance_program?.is_llp_event === false && (!event?.modules || event.modules.length === 0));
  }, [claim?.claim_events]);

  const [moduleAmountPaid, setModuleAmountPaid] = useState(() => {
    const initial = {};
    nonLlpModules.forEach((mod) => {
      initial[`${mod.shop_visit_event_id}_${mod.shop_visit_event_module_id}`] = mod?.claim_amount ?? '';
    });
    return initial;
  });
  const [eventAmountPaid, setEventAmountPaid] = useState(() => {
    const initial = {};
    nonLlpEventsWithoutModules.forEach((event) => {
      initial[event.shop_visit_event_id] = event?.claim_amount ?? '';
    });
    return initial;
  });

  const onFieldChange = (key, value) => { setPayout({ ...payout, [key]: value }) }
  const onModuleAmountChange = (shopVisitEventId, shopVisitEventModuleId, value) => {
    setModuleAmountPaid({ ...moduleAmountPaid, [`${shopVisitEventId}_${shopVisitEventModuleId}`]: value })
  }
  const onEventAmountChange = (shopVisitEventId, value) => {
    setEventAmountPaid({ ...eventAmountPaid, [shopVisitEventId]: value })
  }
  const onFocusChange = (key) => { setError({ ...error, [key]: "" }) }

  const onApproveAmount = () => {
    let validationInputs = {};
  
    validationInputs = {
      ...validationInputs,
      payout_date: !payout?.payout_date ? "Please select Disbursement Date" : !isValidYearRange(payout?.submission_date) ? "Year must be between 1900 and 2100." : "",
    };

    const showClaimError = (toastAmount, label) => {
      enqueueSnackbar(`${label ? `${label} Amount` : 'Verify Amount'} exceeds Claim Amount limit (Max: US$${toastAmount}).`, { variant: 'error', anchorOrigin: { vertical: 'top', horizontal: 'right' } });
    };


    const hasClaimAmount = (value) => value !== null && value !== undefined && value !== '';

    if (shopVisit?.asset_type !== 2) {
      if (hasClaimAmount(claim?.claim_amount) && Number(payout?.amount_paid) > Number(claim.claim_amount)) {
        showClaimError(claim?.claim_amount);
        return;
      }
    } else {
      for (const mod of nonLlpModules) {
        const fieldKey = `${mod.shop_visit_event_id}_${mod.shop_visit_event_module_id}`;
        const enteredAmount = Number(moduleAmountPaid[fieldKey]) || 0;
        if (hasClaimAmount(mod?.claim_amount) && enteredAmount > Number(mod.claim_amount)) {
          showClaimError(mod.claim_amount, mod?.module_name);
          return;
        }
      }
      for (const event of nonLlpEventsWithoutModules) {
        const enteredAmount = Number(eventAmountPaid[event.shop_visit_event_id]) || 0;
        if (hasClaimAmount(event?.claim_amount) && enteredAmount > Number(event.claim_amount)) {
          showClaimError(event.claim_amount, event?.maintenance_program?.name);
          return;
        }
      }
    }
    if (Object.keys(validationInputs).every((k) => { return validationInputs[k] === ""; })) {
      const payoutDate = moment(payout.payout_date);
      const submissionDate = claim?.submission_date ? moment(claim.submission_date) : null;
      const showError = (message) => {
        enqueueSnackbar(message, { variant: "error", anchorOrigin: { horizontal: "right", vertical: "top" }, });
        return true;
      };
      const isOutsideRange = (start, end) => {
        return payoutDate&& ( payoutDate?.isBefore(moment(start)) || payoutDate?.isAfter(moment(end)));
      };
      if (isOutsideRange(leaseBasicDetails.start_date, leaseBasicDetails.end_date)) {
        return showError("Disbursement Date: Must be between lease start date and end date.");
      }
      if (submissionDate && payoutDate.isBefore(submissionDate)) {
        return showError("Disbursement Date: Must be greater than or equal to Submission Date.");
      }
      let payload;
      if (shopVisit?.asset_type === 2 && Array.isArray(claim?.claim_events)) {
        const claimEvents = claim.claim_events.map(event => {
          if (event?.maintenance_program?.is_llp_event === true) {
            return {
              id: event?.id,
              shop_visit_event_id: event?.shop_visit_event_id,
              amount_paid: Number(llpAmountPaid) || 0,
            };
          }
          if (Array.isArray(event?.modules) && event.modules.length > 0) {
            const modules = event.modules.map(mod => ({
              id: mod?.id,
              shop_visit_event_module_id: mod?.shop_visit_event_module_id,
              amount_paid: Number(moduleAmountPaid[`${event.shop_visit_event_id}_${mod.shop_visit_event_module_id}`]) || 0,
            }));
            const amount_paid = modules.reduce((sum, m) => sum + (Number(m.amount_paid) || 0), 0);
            return { id: event?.id, shop_visit_event_id: event?.shop_visit_event_id, amount_paid, modules };
          }
          return {
            id: event?.id,
            shop_visit_event_id: event?.shop_visit_event_id,
            amount_paid: Number(eventAmountPaid[event.shop_visit_event_id]) || 0,
          };
        });
        payload = { payout_date: payout?.payout_date, is_claim_carry_forward: isClaimCarryForward, reference_number: payout?.reference_number, transaction_description: payout?.transaction_description, claim_events: claimEvents };
      } else {
        payload = { ...payout }
        payload.is_claim_carry_forward = isClaimCarryForward;
        if (payout?.amount_paid || claim?.claim_amount) {
          payload.amount_paid = payout.amount_paid ? Number(payout.amount_paid) : Number(claim?.claim_amount)
        }
      }
      if (claim?.id) {
        setLoading({ pageLoader: true })
        globalPutService(verifyAmountApi(params, shopVisit?.asset_type, updatedAsset?.asset_slug, claim?.id), payload).then((response) => {
          if (checkApiStatus(response)) {
            // enqueueSnackbar(response?.data?.message, { variant: "success", anchorOrigin: { horizontal: "right", vertical: "top" } });
            // getResponseBack()
            const payload = { payout_status: 6, id: claim?.id }
                    globalPutService(updateClaimApi(params), payload).then(response => {
                        setLoading({ pageLoader: false });
                        if (checkApiStatus(response)) {
                            getResponseBack()
                            onToggleModal()
                            enqueueSnackbar(response?.data?.message, { variant: "success", hideIconVariant: true, anchorOrigin: { horizontal: "right", vertical: "top" }, });
                            trackActivity('Item Edited', { page_title: `Mark As Paid button clicked`, success_msg: response?.data?.message, item_id: claim?.id, payload: payload, response: response?.data })
                        } else {
                            enqueueSnackbar(response?.data?.message, { variant: "error", hideIconVariant: true, anchorOrigin: { horizontal: "right", vertical: "top" }, });
                            trackActivity('Item Edited', { page_title: `Mark As Paid button clicked`, error_msg: response?.data?.message, item_id: claim?.id, payload: payload, response: response?.data })
                        }
                    })
            trackActivity('Item Edited', { page_title: `Edit Claim`, success_msg: response?.data?.message, item_id: claim?.id, payload: payload, response: response?.data })
          } else {
            setLoading({ pageLoader: false });
            if (response?.data?.statusCode == 400 && response?.data?.status === "ERROR" && response?.data?.data?.length > 0) {
              setServerError({ modal: true, data: response?.data?.data })
            } else enqueueSnackbar(response?.data?.message, { variant: 'error', anchorOrigin: { vertical: 'top', horizontal: 'right' } });
          }
        });

      }
    } else {
      setError(validationInputs)
    }
  };
  const handleItemClicked = (page_type) => {
    trackActivity('Item Clicked', { page_title: ` Shop Visit Edit Disbursement`, page_type: page_type, })
  }
  return (
    <>
      <Dialog
        aria-labelledby="customized-dialog-title"
        className='add-lease-dialog'
        maxWidth={shopVisit?.asset_type === 2 ? "md" :'xs' }
        fullWidth={shopVisit?.asset_type === 2}
        open={verifyAmount?.modal}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} className='dialog-header'>Please Verify Claimable Amount</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {shopVisit?.asset_type !== 2 ? <Grid size={{ md:  12, xs: 12 }}>
              <TextField
                defaultValue={claim?.claim_amount}
                required
                id="amount_paid"
                value={modeType === 'view' && payout?.amount_paid !== '' && payout?.amount_paid !== null && payout?.amount_paid !== undefined ? numberWithCommas(roundAmount(payout?.amount_paid)) : payout?.amount_paid}
                onChange={(e, value) => { regexConstants.decimalnodotstart.test(e.target.value) ? onFieldChange('amount_paid', e.target.value) : e.preventDefault() }}
                label="Claim Amount"
                variant="outlined"
                size="small"
                margin="normal"
                error={!!error?.amount_paid}
                helperText={error?.amount_paid}
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 11 }, input: { startAdornment: (<InputAdornment position="start">$</InputAdornment>) } }}
                onFocus={() => onFocusChange("amount_paid")}
                fullWidth
                disabled={modeType === 'view'}
              />
            </Grid> : null}
            {shopVisit?.asset_type === 2 && isLlpEvent ?
              <Grid size={{ md: 4, xs: 12 }}>
                <TextField
                  disabled={true}
                  id="name"
                  label="LLP Claim Amount"
                  fullWidth
                  size="small"
                  margin="normal"
                  value={llpAmountPaid !== '' && llpAmountPaid !== null && llpAmountPaid !== undefined ? numberWithCommas(roundAmount(llpAmountPaid)) : llpAmountPaid}
                  slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 11 }, input: { startAdornment: (<InputAdornment position="start">$</InputAdornment>) } }}
                />
              </Grid> : null}
            {shopVisit?.asset_type === 2 ?
              <>
                {nonLlpModules?.length > 0 ? (
                  <Grid size={{ md: 12, xs: 12 }} sx={{ mt: 1 }}>
                    <Typography className='section-header-text'><u>Engine PRSV Disbursement Cost Split Details:</u></Typography>
                    <Box className="escalation-container">
                      <Box className="escalation-box">
                        <Box sx={{ display: "flex", flexWrap: "wrap", rowGap: 1.5, columnGap: 3 }}>
                          {nonLlpModules.map((module) => {
                            const fieldKey = `${module.shop_visit_event_id}_${module.shop_visit_event_module_id}`;
                            const rawAmount = moduleAmountPaid[fieldKey];
                            const amount = modeType === 'view' && rawAmount !== '' && rawAmount !== null && rawAmount !== undefined ? numberWithCommas(roundAmount(rawAmount)) : rawAmount;
                            return (
                              <TextField
                                key={fieldKey}
                                id={`${fieldKey}_claim_amount`}
                                label={
                                  module?.is_non_llp ? (
                                    <span style={{ verticalAlign: 'super' }}>
                                      {module?.module_name}
                                      <Tooltip placement='right' title={<span>Non-LLP</span>} arrow>
                                        <InfoIcon fontSize='small' color='primary' style={{ verticalAlign: 'middle', marginLeft: '2px', fontSize: 'medium' }} />
                                      </Tooltip>
                                    </span>
                                  ) : module?.module_name
                                }
                                margin="normal"
                                slotProps={{ inputLabel: { shrink: true },htmlInput: { maxLength: 11 }, input: { startAdornment: (<InputAdornment position="start">$</InputAdornment>) } }}
                                value={amount ?? ''}
                                onChange={(e) => { regexConstants.decimalnodotstart.test(e.target.value) ? onModuleAmountChange(module.shop_visit_event_id, module.shop_visit_event_module_id, e.target.value) : e.preventDefault() }}
                                size="small"
                                disabled={modeType === 'view'}
                                sx={{ width: 150, flexShrink: 0 }}
                              />
                            )
                          })}
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                ) : null}
                {nonLlpEventsWithoutModules.map((event) => {
                  const rawAmount = eventAmountPaid[event.shop_visit_event_id];
                  const amount = modeType === 'view' && rawAmount !== '' && rawAmount !== null && rawAmount !== undefined ? numberWithCommas(roundAmount(rawAmount)) : rawAmount;
                  return (
                    <Grid size={{ md: 4, xs: 12 }} key={event.shop_visit_event_id}>
                      <TextField
                        id={`event_${event.shop_visit_event_id}_amount_paid`}
                        value={amount ?? ''}
                        label={event?.maintenance_program?.name || "Claim Amount"}
                        onChange={(e) => { regexConstants.decimalnodotstart.test(e.target.value) ? onEventAmountChange(event.shop_visit_event_id, e.target.value) : e.preventDefault() }}
                        variant="outlined"
                        size="small"
                        margin="normal"
                        slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 11 }, input: { startAdornment: (<InputAdornment position="start">$</InputAdornment>) } }}
                        fullWidth
                      />
                    </Grid>
                  )
                })}
              </> : null}
            <Grid size={{ md: shopVisit?.asset_type === 2 ?  4 : 12, xs: 12 }}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  required
                  label="Disbursement Date"
                  value={shopVisit?.payout_date ? moment(shopVisit.payout_date) : null}
                  onChange={(data, value) => onFieldChange('payout_date', moment(data)?.isValid() ? moment(data).format(backendDateFormat) : null)}
                  slotProps={{
                    textField: {
                      required: "true",
                      InputLabelProps: { shrink: true },
                      size: "small",
                      margin: "normal",
                      helperText: error?.payout_date,
                      error: error?.payout_date,
                      onFocus: () => { onFocusChange("payout_date") }
                    },
                  }}
                  onFocus={() => { onFocusChange("payout_date") }}
                  sx={{ width: "100%" }}
                  format={fieldDateFormat}
                  disabled={modeType === 'view'}
                />
              </LocalizationProvider>
            </Grid>
            <Grid size={{ md: shopVisit?.asset_type === 2 ? 4 : 12, xs: 12 }}>
              <TextField
                id="reference_number"
                value={payout?.reference_number || ''}
                onChange={(e) => onFieldChange('reference_number', e.target.value)}
                label="Transaction Number"
                variant="outlined"
                size="small"
                margin="normal"
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 8 } }}
                fullWidth
                disabled={modeType === 'view'}
              />
            </Grid>
            <Grid size={{ md: 12, xs: 12 }}>
              <TextField
                id="transaction_description"
                label="Transaction Details"
                variant="outlined"
                size='small'
                margin='normal'
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth
                multiline
                rows={getTextFieldRows(payout?.transaction_description, 3, 65)}
                value={payout?.transaction_description || ''}
                onChange={(e) => onFieldChange('transaction_description', e.target.value)}
                disabled={modeType === 'view'}
              />
            </Grid>
            <Grid size={{ md: 12, xs: 12 }}>
              <Box className="check-item-div" gap={1} sx={{ cursor: modeType === "view" ? "not-allowed" : "pointer" }}>
                <Checkbox sx={{ p: 0 }}
                  checked={isClaimCarryForward}
                  onChange={(e) => onFieldChange('is_claim_carry_forward', e.target.checked)}
                  disableRipple
                />
                <span className="check-item-span">Carry Forward Fund Balance</span>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button className="cancel-btn" variant='outlined' size='small' disabled={isLoading?.buttonLoader} onClick={() => { onToggleModal(); handleItemClicked('Cancel Button') }}> Cancel </Button>
          <Button variant='contained' className='btn-primary' disabled={modeType === 'view'} size='small' onClick={() => { handleItemClicked('Save Button'); onApproveAmount() }}>
            {isLoading?.buttonLoader ? <CircularProgress color='inherit' size={25} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
      {serverError?.modal ? <ServerErrorModal isLLP={isLlpEvent} handleTabChange={() => onTabChange(null, 1)} serverError={serverError} toggleModalFn={() => setServerError({ modal: false, data: [] })} /> : null}

      {isLoading?.pageLoader ? <PageLoader /> : null}
    </>

  )
}

export default VerifyAmountModal